import { SearchModes } from '@/domainLogic/index.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { ADULTS_DEFAULT, URLParameters } from '@/app/common/urlParameters.ts';
import { getClickoutId } from '@/map1SDPToTSX.ts';
import { getSearchModeFromSearchParams } from '@/app/components/search/utils/query2.ts';
import type { SearchInfoData, SearchInfoDetailData } from '@/app/search/types.ts';
import { getTotalNumAdults, getTotalNumChildren, getTotalNumInfants, getTotalNumRooms } from '@/utils/peopleUtils.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import {
  createStayDates,
  getDestinationsType,
  getRoomsDetails,
  type RoomsDetails,
  type StayDates,
} from '@/app/common/starTrack/utils.ts';
import { PAGE_TYPE } from '@/app/common/pageType.ts';
import { getPageTypeFromSearchParams } from '@/app/common/urlParametersUtils.ts';
import { isEvoLayout } from '@/app/common/evoLayout.ts';
import { createDatesParams, createOriginValue } from '@/app/common/memsearch/getSearchParams.ts';
import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';
import { createQueryString } from '@/utils/queryUtils.ts';
import type { CommonData } from '@/app/common/createCommonData.ts';
import { getUserLoyaltyTier } from '@/app/common/Loyalty/utils.ts';
import { createPartialStay } from '@/app/common/starTrack/events/utils/eventsUtils.ts';
import { getOpenTelemetryTraceIdFromTraceparent } from '@/utils/traceIdUtils.ts';
import { BASE_PATH } from '@/app/components/search/utils/path.ts';
import type { Context, PartialStay, Platform } from './utils/starTrack.types.ts';
import { FunnelLayout } from './utils/starTrack.types.ts';

export const SEARCH_REQUESTED = 'SEARCH_REQUESTED';
export const SEARCH_REQUESTED_VERSION = '3.1';
export const SEARCH_REQUESTED_BE = 'SEARCH_REQUESTED_BE';
export const SEARCH_REQUESTED_BE_VERSION = '1.1';

export enum SubSourceType {
  Home = 'S01HPV10S10RR01',
  // SearchResults = 'S01RRV10S10RR01',
  // InspirationDomainHomePageHO = 'S03HPV10S03RR01',
  // InspirationDomainHomePageDP = 'S07HPV10S07RR01',
  // HOtoHO = 'S03RRV10S03RR01',
  // HOtoDP = 'S03RRV10S07RR01',
  // DPtoDP = 'S07RRV10S07RR01',
  // DPtoHO = 'S07RRV10S03RR01',
  HotelDetailDefault = 'S07RRTL0S07PD03',
  HotelDetailRecommendedHotels = 'S10PDB31S10PD01',
  HotelDetailAlternativeDates = 'S10RRV41S10RR01',
  /** TODO: replace with real StarTrack code from analytics team before shipping to production. */
  HotelDetailApexCalendar = 'S07PDMB0S07PD01',
  HotelDetailPriceCalendar = 'S04PDCS1S04PD01',
  HotelDetailDatesSelector = 'S04PDV60S04PD01',
  // RedirectFromDetailPage = 'S04PDV10S04RR01',
  NotSearchPageTOSearchPage = 'S04PDV10S04RR01',
  SearchProductsToSearchProducts = 'S10RRV10S10RR01',
  SearchProductsToSearchDestinations = 'S10RRV10S10OS01',
  SearchDestinationsToSearchProducts = 'S10OSV10S10RR01',
  SearchDestinationsToSearchDestinations = 'S10OSV10S10OS01',
}

export type SearchRequestedParams = {
  searchId: string;
  searchParams: SearchParams;
  searchInfoData: SearchInfoData;
  searchInfoDetailData: SearchInfoDetailData;
  commonData: CommonData;
  localization: Localization;
  context: Context;
  internalHotelId?: string;
  platform: Platform;
  logText: string;
  referrer?: string;
  apexPerimeterFlag: boolean;
};

export type SearchRequestedPayload = {
  searchId: string;
  searchType: string;
  businessProfileId: string;
  bfSubSource: string | null;
  destination: string;
  destinationType: string;
  departure: string | null;
  roomDetails: RoomsDetails;
  roomQty: number;
  adultQty: number;
  childQty: number;
  infantQty: number;
  context: Context;
  extReferenceId: string | null;
  extReferenceType: string | null;
  completeUrl: string;
  travelClass: string | null;
  internalHotelId: number | null;
  msCurrency: string | null;
  msTotalPrice: string | null;
  msMealPlan: string | null;
  msMetaClickOutId: string | null;
  platform: Platform;
  traceId: string;
  partialStay: PartialStay;
  funnelLayout: string;
  ApexPerimeterFlag: boolean;
  referral_url?: string;
  loyaltyTier: string | null;
} & StayDates;

const getSubSource = ({ searchParams }: { searchParams: SearchParams }): string | null => {
  const existingSubSource =
    searchParams[URLParameters.SUBSOURCE] ||
    searchParams[URLParameters.BF_SUB_SOURCE] ||
    searchParams[URLParameters.BF_SUB_SOURCE_VARIANT_1] ||
    searchParams[URLParameters.BF_SUB_SOURCE_VARIANT_2];
  return existingSubSource || null;
};

// SubSource Specification from Giuseppe Stefanoni in this Slack: https://slacklastminutecom.slack.com/archives/C07481Z5XFG/p1733838302793419
// [for both Openx and TSX] for searches executed from the result page that land in the result page: S10RRV10S10RR01
// [for both Openx and TSX] for searches executed from the result page that land in the OPEN SEARCH RESULT PAGE: S10RRV10S10OS01
// [for both Openx and TSX] for searches executed from the OPEN SEARCH RESULT PAGE that land in the result page: S10OSV10S10RR01
// [for both Openx and TSX] for searches executed from the OPEN SEARCH RESULT PAGE that land in the OPEN SEARCH RESULT PAGE: S10OSV10S10OS01
// [for 1SDP] for searches executed from 1SDP detail page (with the widget on top) that land on detail page: S04PDV10S04PD01
// [for 1SDP] for searches executed from the detail page (with the inline widget above the rooms) [just for 1SDP]: S04PDV60S04PD01
// [for both 1SDP and TSX] for searches executed clicking on "Recommended Hotels": S10PDB31S10PD01
// [for 1SDP] for searches executed clicking on Price Calendar feature: S04PDCS1S04PD01
// [for both Openx and TSX] for searches executed clicking on other dates proposed in case of no availability of a particular hotel searched: S10RRV41S10RR01
// [for TSX]for searches executed from TSX detail page that land in TSX result page: S04PDV10S04RR01
type CreateSubSourceForNewSearchParams = {
  oldSearchParams: SearchParams;
  newPageType: PAGE_TYPE;
};
export const createSubSourceForNewSearch = ({
  oldSearchParams,
  newPageType,
}: CreateSubSourceForNewSearchParams): SubSourceType => {
  const oldPageType = oldSearchParams?.[URLParameters.PAGE_TYPE] || PAGE_TYPE.SEARCH;

  if (oldPageType === PAGE_TYPE.SEARCH) {
    return newPageType === PAGE_TYPE.SEARCH
      ? SubSourceType.SearchProductsToSearchProducts
      : SubSourceType.SearchProductsToSearchDestinations;
  } else if (oldPageType === PAGE_TYPE.SEARCH_DESTINATIONS) {
    return newPageType === PAGE_TYPE.SEARCH
      ? SubSourceType.SearchDestinationsToSearchProducts
      : SubSourceType.SearchDestinationsToSearchDestinations;
  }

  return SubSourceType.NotSearchPageTOSearchPage;
};

const createExt = (searchParams: SearchParams) => {
  const extReferenceId = searchParams[URLParameters.EXT_REFERENCE_ID] || '';
  const extReferenceType = searchParams[URLParameters.EXT_REFERENCE_TYPE] || '';

  if (extReferenceId) {
    return {
      extReferenceId,
      extReferenceType: extReferenceType || 'OPEN-SEARCH',
    };
  }

  return { extReferenceId: null, extReferenceType: extReferenceType || null };
};

export const createSearchRequestedPayload = ({
  searchId,
  searchParams,
  searchInfoData,
  searchInfoDetailData,
  commonData,
  localization,
  context,
  internalHotelId,
  platform,
  logText,
  referrer,
  apexPerimeterFlag,
}: SearchRequestedParams): SearchRequestedPayload => {
  const searchIdToUse = searchId || searchParams[URLParameters.SEARCH_ID] || 'unknown';

  const destinationType = searchInfoDetailData?.destination?.destinationType;

  const highlightHotelId = searchParams[URLParameters.HIGHLIGHT_HOTEL_ID];
  const adddays = searchParams[URLParameters.ADD_DAYS];
  const travelClass = searchParams[URLParameters.FLIGHT_CLASS];
  const dateFrom = searchParams[URLParameters.DATE_FROM];
  const dateTo = searchParams[URLParameters.DATE_TO];
  const searchMode = getSearchModeFromSearchParams(searchParams);
  const adults = searchParams[URLParameters.ADULTS] || ADULTS_DEFAULT;
  const ages = searchParams[URLParameters.AGES];
  const origin = searchParams[URLParameters.ORIGIN];
  const datesParams = createDatesParams(adddays ? parseInt(adddays, 10) : 0, dateFrom, dateTo);
  const { datefrom: searchDateFrom, dateto: searchDateTo } = datesParams;
  const searchOrigin = createOriginValue(searchMode, origin);
  const searchType = !searchMode || searchMode === SearchModes.DP || searchMode === SearchModes.TH ? 'DP' : 'H';
  const { businessProfile: businessProfileId } = localization;
  const departure = searchOrigin ? searchOrigin.toString() : null;
  const destination = decodeURIComponent(searchParams?.[URLParameters.DESTINATION] || '').toString();
  const starTrackDestinationType = getDestinationsType(destination, destinationType);
  const bfSubSource = getSubSource({
    searchParams,
  });
  const roomDetails = getRoomsDetails({ ages: (ages && ages.toString()) || '', adults: adults.toString() });
  const completeUrl = `${localization.hostname}${BASE_PATH}?${createQueryString(searchParams)}`;
  // const { isApp } = curiosityParams;
  const internalHotelIdRaw = internalHotelId || highlightHotelId || null;
  const startrackInternalHotelId: number | null = !Number.isNaN(parseInt(internalHotelIdRaw || '', 10))
    ? parseInt(internalHotelIdRaw || '', 10)
    : null;

  const metapayload = {
    msCurrency: searchParams[URLParameters.MS_CURRENCY] || null,
    msTotalPrice: searchParams[URLParameters.MS_TOTAL_PRICE] || null,
    msMealPlan: searchParams[URLParameters.MS_MEAL_PLAN] || null,
    msMetaClickOutId: getClickoutId(searchParams) || searchParams[URLParameters.META_CLICK_OUT_ID] || null,
  };

  const stayDates = createStayDates({ dateFrom: searchDateFrom, dateTo: searchDateTo });
  const traceId = searchInfoData?.searchTraceParent
    ? getOpenTelemetryTraceIdFromTraceparent(searchInfoData.searchTraceParent)
    : '';
  const partialStay = createPartialStay(searchParams);
  const loyaltyTier = getUserLoyaltyTier(commonData);
  const isDpEvoFunnel = isEvoLayout(getPageTypeFromSearchParams(searchParams), localization.configValuesApp);
  const funnelLayout = isDpEvoFunnel ? FunnelLayout.tsxDpEvo : platform || FunnelLayout.tsx;

  const payload = {
    searchId: searchIdToUse,
    searchType,
    businessProfileId,
    bfSubSource,
    destination,
    destinationType: starTrackDestinationType,
    departure,
    roomDetails,
    roomQty: getTotalNumRooms(searchParams[URLParameters.ADULTS]),
    adultQty: getTotalNumAdults(searchParams[URLParameters.ADULTS]) || ADULTS_DEFAULT,
    childQty: getTotalNumChildren(searchParams[URLParameters.AGES]),
    infantQty: getTotalNumInfants(searchParams[URLParameters.AGES]),
    context,
    ...createExt(searchParams),
    completeUrl,
    travelClass: travelClass || null,
    internalHotelId: startrackInternalHotelId,
    platform,
    // ...(isApp ? { appview: 'true' } : {}),
    ...(stayDates.departureDate.from
      ? { ...stayDates }
      : { ...stayDates, departureDate: { from: '', to: stayDates.departureDate.to } }),
    ...metapayload,
    traceId,
    partialStay,
    funnelLayout,
    ApexPerimeterFlag: !!apexPerimeterFlag,
    ...(referrer ? { referral_url: referrer } : {}),
    loyaltyTier: loyaltyTier ? `${loyaltyTier}` : null,
  };

  if (localization.configValuesApp.detailedLoggingEnabled) {
    clientAppLog({
      level: 'INFO',
      codeSourceName: 'searchRequested',
      logMessage: `REF999 logText:${logText}, payload:${JSON.stringify(payload)}`,
      url: completeUrl,
    });
  }

  return payload;
};
